/**
 * The application store — the shared state every module reads from.
 *
 * PHP renders the authoritative initial values into the page (cart count,
 * wishlist ids), and this store mirrors them client-side so the UI can update
 * optimistically without a round trip. The server always wins on conflict:
 * every API response overwrites the store with what the session actually holds.
 */

import { createStore, persist } from '@/lib/store'

export type Theme = 'light' | 'dark'

export interface AppState {
  cartCount: number
  cartSubtotal: number
  wishlistIds: number[]
  theme: Theme
  /** Which overlay is open, if any — only one at a time. */
  openDrawer: 'cart' | 'nav' | 'filters' | null
}

/**
 * Values PHP writes into `<body data-init-*>` on every render.
 *
 * The `init` prefix is load-bearing: these must not share a name with any
 * component selector, or a query like `[data-cart-count]` will match <body>
 * and a renderer will treat the whole document as a badge.
 */
function readServerState(): Pick<AppState, 'cartCount' | 'cartSubtotal' | 'wishlistIds'> {
  const { dataset } = document.body

  const ids = (dataset.initWishlist ?? '')
    .split(',')
    .map((id) => Number.parseInt(id, 10))
    .filter((id) => Number.isFinite(id))

  return {
    cartCount: Number.parseInt(dataset.initCartCount ?? '0', 10) || 0,
    cartSubtotal: Number.parseInt(dataset.initCartSubtotal ?? '0', 10) || 0,
    wishlistIds: ids,
  }
}

function initialTheme(): Theme {
  // The inline script in header.php has already resolved and applied the
  // theme; read it back rather than recomputing, so the two never disagree.
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light'
}

export const app = createStore<AppState>({
  ...readServerState(),
  theme: initialTheme(),
  openDrawer: null,
})

// Only the theme is persisted client-side. Cart and wishlist live in the PHP
// session — duplicating them in localStorage would let the two drift apart.
persist(app, 'atelier:prefs', ['theme'])
